import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { AppDispatch, RootState } from 'store/store';
import DataForm from './tabs/DataForm';
import LogForm from './tabs/LogForm';
import { hasPermission, formatFullName, capitalizeName, transformNullToEmptyString } from 'utils/utils';
import dayjs from 'dayjs';
import { OpTabs } from 'components/customAntd/DLS/OpTabs/OpTabs';
import { DRAWER_WIDTH } from 'constants/ui';
import { OpFormDrawer } from 'components/customAntd/DLS/OpFormDrawer/OpFormDrawer';
import { RedFlag, RedFlagCount, RedFlagData, RedFlagLog } from 'types/redFlagTypes';
import { getRequest, patchRequest, postRequest } from 'api/apiClient';
import RedFlagForm from './tabs/RedFlagForm';
import VisitorForm from './tabs/VisitorForm';
import { DATE_FORMAT, DATE_TIME_AM_PM_FORMAT, DATE_TIME_FORMAT } from 'constants/dates';
import utc from 'dayjs/plugin/utc';
import { notification } from 'antd';
import { fetchRedFlags } from 'store/slices/redFlagSlice';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);

interface RedFlagsDrawerProps {
    open: boolean;
    selectedRedFlag: RedFlag | null;
    onClose: () => void;
    setRedFlags: (setRedFlags: RedFlag[]) => void;
    setBlockCount?: (blockCount: number) => void;
    setWatchCount?: (watchCount: number) => void;
    startDate?: string;
    endDate?: string;
}

const RedFlagsDrawer: React.FC<RedFlagsDrawerProps> = ({ open, selectedRedFlag, onClose, setRedFlags, setBlockCount, setWatchCount, startDate, endDate }) => {
    const dispatch: AppDispatch = useDispatch();

    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);

    const hasRedFlagWrite = hasPermission(tokenScopeList, orgId, 'o', 'redflagconf:w');

    const [activeKey, setActiveKey] = useState<string>('redFlag');
    const [redFlagData, setRedFlagData] = useState<RedFlagData[]>([]);
    const [redFlagLogs, setRedFlagLogs] = useState<RedFlagLog[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [form] = OpForm.useForm();

    const { t } = useTranslation();

    const initialValues = useMemo(() => ({
        ...(selectedRedFlag
            ? {
                level: selectedRedFlag.level,
                levelName: selectedRedFlag.levelName,
                datatype: selectedRedFlag.datatype,
                effectiveDate: dayjs(selectedRedFlag.effectiveDate).isValid()
                    ? dayjs(selectedRedFlag.effectiveDate).utc(true).local().format(DATE_TIME_AM_PM_FORMAT)
                    : null,
                expireDate: dayjs(selectedRedFlag.expireDate).isValid()
                    ? dayjs.utc(selectedRedFlag.expireDate).local()
                    : null,
                visitor: {
                    firstName: capitalizeName(selectedRedFlag.firstName),
                    lastName: capitalizeName(selectedRedFlag.lastName),
                    middleName: capitalizeName(selectedRedFlag.middleName),
                    dateOfBirth: dayjs(selectedRedFlag.dateOfBirth).isValid()
                        ? dayjs(selectedRedFlag.dateOfBirth).format(DATE_FORMAT)
                        : null,
                    identification: capitalizeName(selectedRedFlag.identification),
                },
            } : {
                level: 1,
                effectiveDate: dayjs().local().format(DATE_TIME_AM_PM_FORMAT),
                visitor: {
                    firstName: "",
                    lastName: "",
                    middleName: "",
                    dateOfBirth: null,
                    identification: "",
                }
            }),
        ...(redFlagData
            ? redFlagData.reduce<Record<string, string>>((acc, item) => {
                if (item.data) {
                    acc[`${item.data}_${item.id}`] = item.value ?? "";
                }
                return acc;
            }, {})
            : {}),
    }), [selectedRedFlag, redFlagData]);

    const [formValues, setFormValues] = useState(initialValues);

    useEffect(() => {
        if (selectedRedFlag) {
            setFormValues(initialValues);
        }
    }, [selectedRedFlag, initialValues]);

    const tabItems = [
        {
            key: 'redFlag',
            label: t('redFlag.tabs.redFlag'),
            children: <RedFlagForm />,
            forceRender: true
        },
        {
            key: 'visitor',
            label: t('redFlag.tabs.visitor'),
            children: <VisitorForm />,
            forceRender: true
        },
        ...(selectedRedFlag ? [{
            key: 'data',
            label: t('redFlag.tabs.data'),
            children: <DataForm redFlagData={redFlagData} selectedRedFlag={selectedRedFlag} />,
        }] : []),
        ...(selectedRedFlag ? [{
            key: 'log',
            label: t('redFlag.tabs.log'),
            children: <LogForm selectedRedFlag={selectedRedFlag!} redFlagLogs={redFlagLogs} setRedFlagLogs={setRedFlagLogs} />,
        }] : []),
    ];

    useEffect(() => {
        if (open) {
            setActiveKey('redFlag');
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            setFormValues({
                level: 1,
                effectiveDate: dayjs().local().format(DATE_TIME_AM_PM_FORMAT), // Current time in local format
                visitor: {
                    firstName: "",
                    lastName: "",
                    middleName: "",
                    dateOfBirth: null,
                    identification: "",
                }
            });
            form.resetFields(); // Also reset the form fields
        }
    }, [open, form]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                if (selectedRedFlag) {
                    const redFlagData = await getRequest(`/orgs/${orgId}/redflag/${selectedRedFlag.id}/redFlagData`);
                    setRedFlagData(redFlagData.data);

                    const redFlagLogs = await getRequest(`/orgs/${orgId}/redflag/${selectedRedFlag.id}/redFlagLog`);
                    setRedFlagLogs(redFlagLogs.data);
                }
            } catch (error) {
                console.log("Failed to fetch data.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [orgId, selectedRedFlag]);

    const handleSubmit = useCallback(async ({ values, touchedValues, initialValues }: IOnSubmitArgs) => {
        setIsSubmitting(true);
        try {
            const effectiveDate = dayjs(values.effectiveDate);
            const expireDate = values.expireDate ? dayjs(values.expireDate).endOf('day') : null;

            if (expireDate && effectiveDate && expireDate.isSameOrBefore(effectiveDate)) {
                notification.error({
                    message: t('notification.error'),
                    description: t('redFlag.notification.expireBeforeEffectiveError'),
                    placement: 'bottomRight',
                });
                return;
            }

            const transformedTouchedValues = transformNullToEmptyString(touchedValues);

            if ((transformedTouchedValues && Object.keys(transformedTouchedValues).length > 0) ||
                (Object.keys(values).some((key) => key.includes('_') && values[key] === undefined))) {

                if (selectedRedFlag) {
                    // Red Flag and Visitor
                    if (transformedTouchedValues.level != null || transformedTouchedValues.expireDate != null || transformedTouchedValues.visitor != null) {
                        await patchRequest(`/orgs/${orgId}/redflag/${selectedRedFlag?.id}`, {
                            ...(transformedTouchedValues.level != null && { level: transformedTouchedValues.level }),
                            ...(transformedTouchedValues.expireDate != null && {
                                expireDate: values.expireDate
                                    ? dayjs(values.expireDate).endOf('day').utc().format(DATE_TIME_FORMAT)
                                    : undefined,
                            }),
                            ...(transformedTouchedValues.visitor != null && {
                                visitor: {
                                    ...(transformedTouchedValues.visitor.firstName != null && { firstName: values.visitor.firstName }),
                                    ...(transformedTouchedValues.visitor.lastName != null && { lastName: values.visitor.lastName }),
                                    ...(transformedTouchedValues.visitor.middleName != null && { middleName: values.visitor.middleName }),
                                    ...(transformedTouchedValues.visitor.dateOfBirth != null && {
                                        dateOfBirth: values.visitor.dateOfBirth
                                            ? dayjs(values.visitor.dateOfBirth).format(DATE_FORMAT)
                                            : undefined,
                                    }),
                                    ...(transformedTouchedValues.visitor.identification != null && { identification: values.visitor.identification }),
                                    // ...(transformedTouchedValues.visitor.address != null && { address: values.visitor.address }),
                                    // ...(transformedTouchedValues.visitor.city != null && { middleName: values.visitor.city }),
                                    // ...(transformedTouchedValues.visitor.postalCode != null && { middleName: values.visitor.postalCode }),

                                },
                            }),

                        });
                    }

                    // Red Flag Data
                    if (Object.keys(values).some((key) => key.includes('_'))) {
                        Object.entries(values).forEach(([key, value]) => {
                            if (key.includes('_')) {
                                const originalKey = key.split('_')[0];
                                const existingData = redFlagData.find(d => `${d.data}_${d.id}` === key);
                                if (existingData) {
                                    // if (values[key] !== "") {
                                    patchRequest(`/orgs/${orgId}/redflag/${selectedRedFlag?.id}/redFlagData/${existingData.id}`, {
                                        name: originalKey,
                                        value: values[key] || ''
                                    });
                                    // } else {
                                    // patchRequest(`/orgs/${orgId}/redflag/${selectedRedFlag?.id}/redFlagData/${existingData.id}`, {
                                    //     name: originalKey,
                                    //     value: values[key],
                                    //     status: 0
                                    // });
                                    // }
                                } else {
                                    postRequest(`/orgs/${orgId}/redflag/${selectedRedFlag?.id}/redFlagData`, {
                                        name: originalKey,
                                        value: values[key] || ''
                                    });
                                }
                            }
                        });
                    }

                    notification.success({
                        message: t('notification.success'),
                        description: t('redFlag.notification.updateSuccess'),
                        placement: 'bottomRight',
                    });
                } else { // create red flag
                    // Red Flag and Visitor
                    await postRequest(`/orgs/${orgId}/redflag`, {
                        ...(values.level != null && { level: values.level }),
                        ...(transformedTouchedValues.expireDate != null && {
                            expireDate: values.expireDate
                                ? dayjs(values.expireDate).endOf('day').utc().format(DATE_TIME_FORMAT)
                                : undefined,
                        }),
                        ...(transformedTouchedValues.visitor != null && {
                            visitors: [{
                                ...(transformedTouchedValues.visitor.firstName != null && { firstName: values.visitor.firstName }),
                                ...(transformedTouchedValues.visitor.lastName != null && { lastName: values.visitor.lastName }),
                                ...(transformedTouchedValues.visitor.middleName != null && { middleName: values.visitor.middleName }),
                                ...(transformedTouchedValues.visitor.dateOfBirth != null && {
                                    dateOfBirth: values.visitor.dateOfBirth
                                        ? dayjs(values.visitor.dateOfBirth).format(DATE_FORMAT)
                                        : undefined,
                                }),
                                ...(transformedTouchedValues.visitor.identification != null && { identification: values.visitor.identification }),
                            }],
                        }),
                    });

                    notification.success({
                        message: t('notification.success'),
                        description: t('redFlag.notification.createSuccess'),
                        placement: 'bottomRight',
                    });
                }

                // Re-fetch the updated list of red flags
                // Use /redFlagActivity if date range is provided (for reports), otherwise use /redflag (for general usage)
                const redFlagsEndpoint = startDate && endDate
                    ? `/orgs/${orgId}/redFlagActivity?startDate=${startDate}&endDate=${endDate}`
                    : `/orgs/${orgId}/redflag`;
                const redFlags = await getRequest(redFlagsEndpoint);
                setRedFlags(redFlags.data);
                await dispatch(fetchRedFlags({ orgId }));

                if (setBlockCount && setWatchCount) {
                    const redFlagCount = await getRequest(`/orgs/${orgId}/redflagCount`);
                    const blockItem = redFlagCount.data.find((item: RedFlagCount) => item.level === 1);
                    const watchItem = redFlagCount.data.find((item: RedFlagCount) => item.level === 2);
                    setBlockCount(blockItem?.count ? blockItem.count : 0);
                    setWatchCount(watchItem?.count ? watchItem.count : 0);
                }
            }
            form.resetFields();
            onClose();
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('redFlag.notification.saveError'),
                placement: 'bottomRight',
            });
            console.error("Form submission failed:", error);
        } finally {
            setIsSubmitting(false);
        }
    }, [dispatch, form, onClose, t, orgId, redFlagData, selectedRedFlag, setRedFlags, setBlockCount, setWatchCount, startDate, endDate]);

    return (
        <OpFormDrawer
            form={form}
            title={selectedRedFlag ? formatFullName(selectedRedFlag?.firstName || null,
                selectedRedFlag?.middleName || null,
                selectedRedFlag?.lastName || null) : t('redFlag.addTitle')}
            width={DRAWER_WIDTH}
            open={open}
            onClose={onClose}
            isFormLoading={loading || isSubmitting}
            isFormReadOnly={!hasRedFlagWrite}
            formComponent={
                <OpForm
                    form={form}
                    initialValues={formValues}
                    onSubmit={handleSubmit}
                    hasError={false}
                    defaultButtons={false}
                    isReadOnly={!hasRedFlagWrite}
                >
                    <OpTabs activeKey={activeKey} onChange={setActiveKey} items={tabItems} />
                </OpForm>
            }
        />
    );
};

export default RedFlagsDrawer;


